<template>
  <div
    :class="{
      'header__wrapper-default--no-burger': isPlatformApp,
    }"
    class="header__wrapper-default">
    <slot name="logo" />

    <div class="header__games">
      <button
        class="button button-additional header__games-button"
        @click="setMenuState('setGameMenuState', !isGameMenuOpened)">
        <span>Choose a game</span>
        <arrowhead-icon
          :is-reverse="isGameMenuOpened"
          class="header__games-arrow" />
      </button>
    </div>

    <p
      v-if="!(user && user.cashback_balance)"
      class="header__advantage-text">
      THE LEADING PLATFORM FOR IN-GAME SERVICES FROM PRO PLAYERS
    </p>

    <products-search
      v-if="isVisibleSearchIcon"
      class="header__search"
      @close="$emit('setBurgerState', false)" />

    <div
      :class="{'header__account--unauthorized': !isCustomerAuthorized}"
      class="header__account">
      <header-select-widget
        :value="activeRegion"
        :items="RegionModel.LIST"
        type="region"
        class="header__region"
        @on-select="setRegion"
        @open="$emit('hideSidebar')" />

      <template v-if="isCustomerAuthorized">
        <div
          v-if="user.cashback_balance"
          class="header__account-user">
          <img
            :src="`/images/icons/roles/1.svg`"
            width="24"
            height="24"
            class="dropdown-widget__icon">

          <cashback-badge
            type="account"
            class="header__account-cashback" />
        </div>

        <header-select-widget
          v-if="!isPlatformApp"
          :items="filteredAccountLinks"
          :value="filteredAccountLinks[0]"
          :open-triggers="['click']"
          type="account"
          should-hide-selected-value
          @select-item="$event.callback && $event.callback()"
          @open="$emit('hideSidebar')" />
      </template>

      <div
        v-else-if="!isPlatformApp && !isBecomePro"
        class="header__login">
        <button
          class="button button-bordered button-bordered--main header__login-button"
          @click="showLoginPopup(SignInModel.POPUP_VIEWS.SIGN_IN)">
          Log in
        </button>

        <button
          class="button button-main header__login-button"
          @click="showLoginPopup(SignInModel.POPUP_VIEWS.JOIN)">
          Sign up
        </button>
      </div>
    </div>

    <div
      v-if="!isPlatformApp"
      class="header__burger">
      <slot name="burger" />
    </div>

    <transition name="transition--opacity">
      <region-tooltip
        v-show="showRegionTooltip"
        class="region-select__tooltip"
        @close="showRegionTooltip = false" />
    </transition>

    <template v-if="!isMobile">
      <transition name="transition--opacity">
        <main-header-p-c-menu-static
          v-if="!isMobile && isBurgerActive"
          class="header__static-links"
          @close="closeHeaderMenu" />
      </transition>
    </template>

    <slot
      v-if="!isMobile"
      name="pc-game-menu" />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import RegionModel from 'Models/RegionModel';
import SignInModel from 'Models/SignInModel';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import ArrowheadIcon from 'Components/ui/Icons/ArrowheadIcon.vue';
import ProductsSearch from 'Root/pages/game/components/ProductsSearch.vue';
import HeaderSelectWidget from 'Components/MainHeader/HeaderSelectWidget.vue';
import RegionTooltip from 'Components/RegionTooltip.vue';
import SignInPopup from 'Components/Popups/SignInPopup.vue';
import CashbackBadge from 'Components/CashbackBadge.vue';

const pagesWithoutTooltips = [
  'about',
  'agreement',
  'agreement-coach',
  'agreement-customer',
  'become-pro',
  'contacts',
  'cookie',
  'sitemap',
  'privacy',
  'booster__register',
  'blog',
];

export default {
  name: 'DefaultHeaderTemplate',

  components: {
    ProductsSearch,
    ArrowheadIcon,
    HeaderSelectWidget,
    RegionTooltip,
    CashbackBadge,
    MainHeaderPCMenuStatic: () => import('Components/MainHeader/MainHeaderPCMenuStatic.vue'),
  },

  props: {
    isGameMenuOpened: {
      type: Boolean,
      default: false,
    },

    isBurgerActive: {
      type: Boolean,
      default: false,
    },

    isMobile: {
      type: Boolean,
      default: true,
    },

    isCustomerAuthorized: {
      type: Boolean,
      default: false,
    },

    filteredAccountLinks: {
      type: Array,
      default: () => ([]),
    },
  },

  data() {
    return {
      showRegionTooltip: false,
      RegionModel,
      SignInModel,
    };
  },

  computed: {
    ...mapState(['userDevice', 'user', 'activeGame', 'activeRegion']),
    ...mapGetters(['isPlatformApp']),

    isVisibleSearchIcon() {
      /* Показываем иконку поиска только на "игровых" роутах */
      return this.$route.name.startsWith('game__');
    },

    isBecomePro() {
      return this.$route.name === 'become-pro';
    },
  },

  watch: {
    activeGame(newVal) {
      if (newVal) {
        this.setRegionTooltipState();
      }
    },

    showRegionTooltip(newVal) {
      if (newVal) {
        this._skGlobal.EVENTS.POPUP.OPEN.dispatch();
      } else {
        this._skGlobal.EVENTS.POPUP.CLOSE.dispatch();
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.setRegionTooltipState();

      this.$watch('$route.name', () => {
        this.setRegionTooltipState();
      });
    });
  },

  methods: {
    ...mapMutations(['mutateActiveRegion']),

    setMenuState(menu, state) {
      this.$emit(menu, state);
    },

    setRegionTooltipState() {
      const shouldShowTooltipAtThisPage = !pagesWithoutTooltips
        .some((page) => this.$route.matched.some((route) => route.name === page));

      const isRegionConfirmed = 'localStorage' in window
        ? +localStorage.getItem(RegionModel.REGION_COOKIE)
        : false;

      const isMobile = this.userDevice.innerWidth < 640;

      this.showRegionTooltip = !isRegionConfirmed && shouldShowTooltipAtThisPage && !isMobile;

      this.$emit('setVisibleRegionTooltip', this.showRegionTooltip);
    },

    setBurgerState(state) {
      if (!this.isMobile) {
        this.setMenuState('setBurgerState', state);
        return;
      }

      this.setMenuState('setBurgerState', false);
      this.setMenuState('setGameMenuState', !this.isBurgerActive);
    },

    closeHeaderMenu() {
      if (!this.isMobile) {
        this.setMenuState('setBurgerState', false);
        this.setMenuState('setGameMenuState', false);
      }
    },

    setRegion(region) {
      this.mutateActiveRegion(region.id);
      this.$cookie.set(RegionModel.REGION_COOKIE, region.id, { expires: '5Y' });
      localStorage.setItem(RegionModel.REGION_COOKIE, region.id);
      googleTagAnalytics.emitSelectRegion(region.shortTitle);
    },

    showLoginPopup(view) {
      this.$vuedals.openMobilePanel({
        class: 'sign-in-popup',
        component: SignInPopup,
        data: {
          initialView: view || SignInModel.POPUP_VIEWS.SIGN_IN,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  .header__wrapper-default {
    grid-template-areas: 'logo search account burger';
    grid-template-columns: 1fr max-content auto max-content;
  }

  .header__wrapper-default--no-burger {
    grid-template-areas: 'logo search account';
    grid-template-columns: 1fr max-content auto;
  }

  .header__games {
    grid-area: games;
    display: none;
  }

  .header__advantage-text {
    grid-area: text;
    display: none;
  }

  .header__search {
    grid-area: search;
  }

  .header__account {
    grid-area: account;
    display: grid;
    grid-auto-flow: column;
    grid-gap: 14px;
    align-items: center;
    position: relative;
  }

  .header__account-user {
    display: flex;
  }

  .header__account-cashback {
    margin-left: 8px;
  }

  .header__region {
    margin-left: -13px; /* anti padding .ui-select__head */
    margin-right: -13px;
  }

  .header__login {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    grid-column-gap: 16px;
  }

  .header__login-button {
    padding: 8px 12px;
    font-size: 13px;
  }

  //.header__login-button:first-child {
  //  background: none;
  //  border: 1px solid $color--basic-green;
  //}

  @media screen and #{$screen-less-than-480} {
    .header__region {
      display: none;
    }

    .header__login-button:not(:first-child) {
      display: none;
    }
  }

  @media screen and #{$screen-less-than-640} {
    .header__account-user {
      display: none;
    }

    .region-select__tooltip {
      position: fixed;
      bottom: 15px;
      width: auto;
      left: 13px;
      right: 13px;
    }
  }

  @media screen and #{$screen-more-or-640} {
    .header__login-button {
      padding: 12px;
    }

    .region-select__tooltip {
      position: absolute;
      top: 100%;
      margin-top: -7px;
      right: var(--padding);
    }
  }

  @media screen and #{$screen-more-or-1024} {
    .header__wrapper-default {
      grid-template-areas: 'logo games search account burger';
      grid-template-columns: max-content 1fr max-content auto max-content;
    }

    .header__wrapper-default--no-burger {
      grid-template-areas: 'logo games search account account';
      grid-template-columns: max-content 1fr max-content auto;
    }

    .header__games {
      display: block;
    }

    .header__games-button {
      font-size: 13px;
      display: flex;
      align-items: center;
      padding: 12px;
      white-space: nowrap;
    }

    .header__games-arrow {
      --width: 50%;

      width: 16px;
      height: 16px;
      margin-left: 5px;
    }
  }

  @media screen and #{$screen-more-or-1024} {
    .header__wrapper-default {
      grid-template-areas: 'logo games text search account burger';
      grid-template-columns: max-content max-content auto 1fr auto max-content;
    }

    .header__wrapper-default--no-burger {
      grid-template-areas: 'logo games text search account';
      grid-template-columns: max-content max-content auto 1fr auto;
    }

    .header__search {
      display: initial;
      margin-left: auto;
      z-index: 10;
    }

    .header__advantage-text {
      font-size: 11px;
      font-weight: 500;
      margin-right: auto;
      text-transform: uppercase;
      max-width: 22em;
      letter-spacing: 0.1em;
      line-height: 15px;
      display: block;
    }

    .header__static-links {
      position: absolute;
      z-index: 5;
      top: calc(50% - 32px); /* 32px подобрано вручную */
      right: calc(var(--padding) - 12px); /* 12px отступ от бургера */
      font-weight: 500;
    }
  }

  @media screen and #{$screen-more-or-1366} {
    .header__advantage-text {
      font-size: 13px;
    }
  }

  @media screen and #{$screen-more-or-1440} {
    .header__wrapper-default, .header__account {
      grid-gap: 40px;
    }
  }
</style>
